import {DI_TOKENS} from '@/di/di.tokens';
import {ShareStatus} from '@/stores/ShareStore/ShareStore.constants';
import {ShareState} from '@/stores/ShareStore/ShareStore.types';

import {injected} from 'brandi';
import copy from 'copy-to-clipboard';

import {LoggerService} from '@/services/LoggerService/LoggerService';
import {PlatformService} from '@/services/PlatformService/PlatformService';
import {QueryStringService} from '@/services/QueryStringService/QueryStringService';
import {ShareStore} from '@/stores/ShareStore/ShareStore';
import {UserStore} from '@/stores/UserStore/UserStore';
import {Envs, GlobalWindow} from '@/types';

export class ShareService {
  private readonly timeout = 3000;
  private hideTimeoutFriendRef: number = 0;

  constructor(
    private readonly envs: Envs,
    private readonly globalWindow: GlobalWindow,
    private readonly shareStore: ShareStore,
    private readonly userStore: UserStore,
    private readonly queryStringService: QueryStringService,
    private readonly loggerService: LoggerService,
    private readonly platformService: PlatformService,
  ) {}

  public startFriendRefShare = async (): Promise<void> => {
    if (this.canShare()) {
      void this.shareFriendRefNative();
    } else {
      this.copyFriendRefLink();
    }
  };

  public canShare = (): boolean => {
    // FYI: механизм шера работает только под https
    // @See: https://developer.mozilla.org/en-US/docs/Web/API/Web_Share_API
    const canShare = this.globalWindow.navigator?.canShare?.({url: this.envs.APP_PROJECT_URL});

    if (canShare === undefined) {
      return false;
    }

    return canShare && this.platformService.isApp();
  };

  private shareFriendRefNative = async (): Promise<void> => {
    const shareUrl = this.createFriendRefUrl();
    if (!shareUrl) {
      return;
    }

    const newShareState = await this.shareNative(shareUrl);
    this.shareStore.setFriendRefShareStatus(newShareState);
    this.hideTimeoutFriendRef = this.globalWindow.setTimeout(() => {
      this.shareStore.setFriendRefShareIdle();
    }, this.timeout);
  };

  private copyFriendRefLink = (): void => {
    if (this.shareStore.isShareStatusSuccess(this.shareStore.getFriendRefShareState())) {
      return;
    }

    const shareUrl = this.createFriendRefUrl();
    if (!shareUrl) {
      return;
    }

    this.globalWindow.clearTimeout(this.hideTimeoutFriendRef);

    const newShareState = this.copyToClipboard(shareUrl);

    this.shareStore.setFriendRefShareStatus(newShareState);
    this.hideTimeoutFriendRef = this.globalWindow.setTimeout(() => {
      this.shareStore.setFriendRefShareIdle();
    }, this.timeout);
  };

  private copyToClipboard = (shareUrl: string): ShareState => {
    try {
      copy(shareUrl);
      return {status: ShareStatus.Success};
    } catch (error: unknown) {
      this.loggerService.logCopyToClipboardError(error as Error);
      return {status: ShareStatus.UnknownError, error: error};
    }
  };

  private shareNative = async (shareUrl: string): Promise<ShareState> => {
    try {
      await this.globalWindow.navigator?.share?.({url: shareUrl});
      return {status: ShareStatus.Success};
    } catch (error: DOMException | unknown) {
      this.loggerService.logNativeShareError(error as DOMException);
      // @see: https://stackoverflow.com/questions/60510383/web-share-api-navigator-share-problem-when-canceling-share-dialog-ios-and-androi
      if (error?.toString().includes('AbortError')) {
        return {status: ShareStatus.Aborted};
      } else {
        return {status: ShareStatus.UnknownError};
      }
    }
  };

  private createFriendRefUrl = (): string | void => {
    const refId = this.userStore.getUserId();
    return this.queryStringService.createFriendRefUrl(refId || '');
  };
}

injected(
  ShareService,
  DI_TOKENS.envs,
  DI_TOKENS.globalWindow,
  DI_TOKENS.shareStore,
  DI_TOKENS.userStore,
  DI_TOKENS.queryStringService,
  DI_TOKENS.loggerService,
  DI_TOKENS.platformService,
);
